/**
 * N1 Verlust-Bilanz (Workshop-Lücken 29.10.2026, Plan `.claude/plans/2026-10-29-workshop-luecken.md` Paket U, Q1 Till):
 * waagrechte Brücke von den Bestellungen bis zu den bezahlten Anfos, Anzahl und Anteil je Stufe, das Vorjahr als blasser
 * Balken unter jeder Stufe. Euro NUR an zwei Ankern (oben Erwarteter Wert aller Anfos, unten bisher bezahlt), verweigerte
 * nur als Anzahl. Backend `/api/auswertungen/umsatz/verlust-bilanz` (`verlustBilanzService.ts`).
 * Reines HTML mit Prozent-Breiten (390 px ohne Querscrollen), keine Animation.
 */
import { FC, ReactNode } from 'react';
import { Alert, Box } from '@mui/material';
import ChartCard from '../../../components/auswertungen/ChartCard';
import { BefundZeile, Hinweis, int, Laden, spanne, tEuro, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import { rechnungsstufeFarbe, UNSICHER, unsicherFuellung, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchVerlustBilanz, type Bilanz, type VerlustStufeKey } from '../../../helpers/umsatzData';
import { DatenstandText, Kartenkopf, letzterTag } from './teile';

/** Stufen der Brücke: Begriff und Farbe wie in der Rechnungsstufen-Karte (`rechnungsstufeFarbe`: bezahlt blau, verloren orange). */
const STUFEN: { key: VerlustStufeKey; term: { label: string; short: string; tip: string } }[] = [
  { key: 'no_invoice_running', term: TERMS.stufeRechnungKommt },
  { key: 'no_invoice_ended', term: TERMS.stufeVorErsterRechnung },
  { key: 'invoiced_unpaid', term: TERMS.stufeRechnungOffen },
  { key: 'trial_only', term: TERMS.stufeNurTestphase },
  { key: 'paid', term: TERMS.stufeBezahlt },
];

/** Segment je Stufe in Prozent der Anfos mit bekanntem Zahlstand: Verluste schwimmen (Rest davor → Rest danach), bezahlt steht ab 0. */
export function brueckenSegmente(b: Pick<Bilanz, 'stufen'>): Record<VerlustStufeKey, { von: number; bis: number }> {
  const out = {} as Record<VerlustStufeKey, { von: number; bis: number }>;
  let rest = 100;
  for (const s of b.stufen) {
    const a = s.anteil ?? 0;
    if (s.key === 'paid') out[s.key] = { von: 0, bis: a };
    else {
      out[s.key] = { von: Math.max(0, rest - a), bis: rest };
      rest -= a;
    }
  }
  return out;
}

const Zeile: FC<{ label: ReactNode; rechts: ReactNode; balken?: ReactNode }> = ({ label, rechts, balken }) => (
  <Box sx={{ py: 0.75, borderTop: 1, borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', columnGap: 2, rowGap: 0.25, fontSize: 13 }}>
      <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>{label}</Box>
      {/* ml auto: bricht die Zeile um (390 px), stehen alle Werte rechtsbündig untereinander statt mal links, mal rechts. */}
      <Box sx={{ ml: 'auto', textAlign: 'right', color: 'text.secondary', fontSize: 12, lineHeight: 1.35 }}>{rechts}</Box>
    </Box>
    {balken}
  </Box>
);

/** `unsicher` (D6, „Rechnung kommt noch"): Farbe mit geringer Deckkraft und Kontur. */
const Balken: FC<{ von: number; bis: number; farbe: string; unsicher?: boolean; vj?: { von: number; bis: number } }> = ({ von, bis, farbe, unsicher, vj }) => (
  <Box sx={{ position: 'relative', height: vj ? 22 : 14, mt: 0.5 }}>
    <Box sx={{ position: 'absolute', left: 0, right: 0, top: 0, height: 14, borderRadius: '3px', backgroundColor: 'action.hover' }} />
    <Box
      data-unsicher={unsicher && bis > von ? 'kontur' : undefined}
      sx={{
        position: 'absolute',
        left: `${von}%`,
        width: `${Math.max(0, bis - von)}%`,
        top: 0,
        height: 14,
        borderRadius: '3px',
        boxSizing: 'border-box',
        ...(unsicher ? { backgroundColor: unsicherFuellung(farbe), border: bis > von ? `${UNSICHER.kontur}px solid ${farbe}` : 0 } : { backgroundColor: farbe }),
      }}
    />
    {vj && <Box sx={{ position: 'absolute', left: `${vj.von}%`, width: `${Math.max(0, vj.bis - vj.von)}%`, top: 17, height: 5, borderRadius: '2px', backgroundColor: farbe, opacity: 0.35 }} />}
  </Box>
);

const fenster = (b: Bilanz) => spanne(b.von, letzterTag(b.bis));

const VerlustBilanzKarte: FC = () => {
  const { data, fehler } = useLaden(fetchVerlustBilanz);
  const viz = useVizStyle();
  const j = data?.jetzt;
  const v = data?.vorjahr;
  const segJ = j ? brueckenSegmente(j) : null;
  const segV = v ? brueckenSegmente(v) : null;
  const stufeVj = (k: VerlustStufeKey) => v?.stufen.find((s) => s.key === k);
  // Befund: größte Stufe ohne Zahlung (Anteil), dazu Bezahlt gegen Vorjahr.
  const verluste = j ? j.stufen.filter((s) => s.key !== 'paid') : [];
  const groesste = verluste.reduce<(typeof verluste)[number] | null>((m, s) => (m === null || (s.anteil ?? 0) > (m.anteil ?? 0) ? s : m), null);
  const bezahlt = j?.stufen.find((s) => s.key === 'paid');
  const bezahltVj = stufeVj('paid');
  const groessteTerm = STUFEN.find((s) => s.key === groesste?.key)?.term;

  return (
    <ChartCard
      title="Wo Bestellungen verloren gehen"
      anker="verlust-bilanz"
      howToRead="Von oben nach unten: alle Bestellungen, was PL-X verweigert hat, und was aus den Anfos geworden ist. Jeder Balken zeigt, welcher Teil der Anfos an dieser Stufe hängen bleibt; der blasse Strich darunter ist derselbe Zeitraum im Vorjahr. Unten bleibt, wer bezahlt hat. „Bisher bezahlt“ kann über dem Erwarteten Wert liegen: der rechnet mit dem heutigen Angebotswert, frühere Jahrgänge zahlten mehr."
      terms={[
        { label: TERMS.verlustBilanz.label, tip: TERMS.verlustBilanz.tip },
        { label: TERMS.bestellungenAlle.label, tip: TERMS.bestellungenAlle.tip },
        { label: TERMS.verweigert.label, tip: TERMS.verweigert.tip },
        ...STUFEN.map((s) => ({ label: s.term.label, tip: s.term.tip })),
        { label: TERMS.wertBeiBestellung.label, tip: TERMS.wertBeiBestellung.tip },
        { label: TERMS.umsatzNetto.label, tip: TERMS.umsatzNetto.tip },
        { label: TERMS.reife.label, tip: TERMS.reife.tip },
        { label: TERMS.vorlaeufig.label, tip: TERMS.vorlaeufig.tip },
      ]}
      source="PL-X-Bestellungen und Zahlungen (auch verweigerte), Anfo-Werte, PX-Data-Export"
      freshnessKey={['plx_orders', 'anfo_werte']}
    >
      {!data || !j || !v || !segJ || !segV ? (
        <Laden fehler={fehler} />
      ) : !j.summe_ok || !v.summe_ok ? (
        <Alert severity="warning">
          Die Stufen ergeben nicht genau die Anfos ({j.summe_ok ? 'Vorjahr' : 'dieses Jahr'}) — die Bilanz wird deshalb nicht gezeigt. Bitte melden.
        </Alert>
      ) : (
        <>
          <BefundZeile
            text={
              groesste && groessteTerm && bezahlt
                ? `Die meisten Anfos gehen vor der ersten Zahlung bei „${groessteTerm.short}“ verloren: ${formatPct(groesste.anteil, 0)} (Vorjahr ${formatPct(stufeVj(groesste.key)?.anteil ?? null, 0)}). Bezahlt haben ${formatPct(bezahlt.anteil, 0)} (Vorjahr ${formatPct(bezahltVj?.anteil ?? null, 0)}).`
                : undefined
            }
            fehlt={groesste ? null : 'Keine Anfos im Zeitraum.'}
          />
          <Kartenkopf>
            Bestellungen {fenster(j)} gegen {fenster(v)} · nur Anfos ab {data.reife_tage} Tagen · Stand <DatenstandText ds={data.datenstand} />
          </Kartenkopf>
          <Zeile
            label={<Box sx={{ fontWeight: 600 }}>{TERMS.wertBeiBestellung.label} aller Anfos</Box>}
            rechts={
              <>
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 13 }}>
                  {tEuro(j.erwarteter_wert)}
                </Box>{' '}
                · Vorjahr {tEuro(v.erwarteter_wert)}
              </>
            }
          />
          <Zeile
            label={TERMS.bestellungenAlle.label}
            rechts={
              <>
                {int(j.bestellungen)} · Vorjahr {int(v.bestellungen)}
              </>
            }
          />
          <Zeile
            label={<>− {TERMS.verweigert.short} (keine Anfo)</>}
            rechts={
              <>
                {int(j.verweigert)} = {formatPct(j.verweigert_anteil)} der Bestellungen · Vorjahr {formatPct(v.verweigert_anteil)}
              </>
            }
          />
          <Zeile
            label={<Box sx={{ fontWeight: 600 }}>= {TERMS.anfos.label}</Box>}
            rechts={
              <>
                {int(j.anfos)} · Vorjahr {int(v.anfos)}
                {(j.vorlaeufig > 0 || v.vorlaeufig > 0) && (
                  <Box component="span" sx={{ display: 'block' }}>
                    davon vorläufig (Zahlstand unbekannt, in keinem Anteil) {int(j.vorlaeufig)} · Vorjahr {int(v.vorlaeufig)}
                  </Box>
                )}
              </>
            }
            balken={<Balken von={0} bis={100} farbe={viz.muted} vj={{ von: 0, bis: 100 }} />}
          />
          {STUFEN.map((s) => {
            const sj = j.stufen.find((x) => x.key === s.key);
            const sv = stufeVj(s.key);
            return (
              <Zeile
                key={s.key}
                label={
                  <Box component="span" sx={{ fontWeight: s.key === 'paid' ? 600 : 400 }}>
                    {s.key === 'paid' ? '= ' : '− '}
                    {s.term.label}
                  </Box>
                }
                rechts={
                  <>
                    <Box component="span" sx={{ color: 'text.primary', fontWeight: 600, fontSize: 13 }}>
                      {formatPct(sj?.anteil ?? null)}
                    </Box>{' '}
                    ({int(sj?.n ?? 0)}) · Vorjahr {formatPct(sv?.anteil ?? null)} ({int(sv?.n ?? 0)})
                  </>
                }
                balken={<Balken von={segJ[s.key].von} bis={segJ[s.key].bis} {...rechnungsstufeFarbe(viz, s.key)} vj={segV[s.key]} />}
              />
            );
          })}
          <Zeile
            label={<Box sx={{ fontWeight: 600 }}>{TERMS.umsatzNetto.label}</Box>}
            rechts={
              <>
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', fontSize: 13 }}>
                  {tEuro(j.bisher_bezahlt)}
                </Box>{' '}
                · Vorjahr {tEuro(v.bisher_bezahlt)}
              </>
            }
          />
          <Hinweis>
            Anteile über die {int(j.bekannt)} Anfos mit bekanntem Zahlstand (Vorjahr {int(v.bekannt)}); zusammen ergeben die Stufen 100 %. Nur
            Bestellungen, die am Stichtag mindestens {data.reife_tage} Tage alt sind, deshalb endet der Zeitraum{' '}
            {data.reife_tage} Tage vor heute. Euro steht nur oben und unten: der Erwartete Wert rechnet schon ein, dass viele Anfos nie zahlen —
            ein Euro-Betrag je Stufe wäre kein entgangenes Geld. „{TERMS.umsatzNetto.label}“ wächst mit dem Alter: die Anfos des Vorjahrs hatten ein
            Jahr länger Zeit zu zahlen. Verweigerte Bestellungen nur als Anzahl, sie sind keine Anfos.
          </Hinweis>
        </>
      )}
    </ChartCard>
  );
};

export default VerlustBilanzKarte;
